import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/portal/Navbar";
import Footer from "../../components/portal/Footer";
import PropertyCard from "../../components/portal/PropertyCard";
import WhatsAppBtn from "../../components/portal/WhatsAppBtn";
import { portalAPI, imgUrl } from "../../api";
import { useSettings } from "../../context/SettingsContext";
import { TIPOS_PROPIEDAD, CIUDADES } from "../../utils";
import "./Inicio.css";

export default function Inicio() {
  const [destacadas, setDestacadas] = useState([]);
  const [stats, setStats] = useState(null);
  const [busqueda, setBusqueda] = useState({ operation: "VENTA", type: "", city: "" });
  const navigate = useNavigate();
  const { settings } = useSettings();

  useEffect(() => {
    portalAPI.destacadas().then((r) => setDestacadas(r.data.data)).catch(() => {});
    portalAPI.stats().then((r) => setStats(r.data.data)).catch(() => {});
  }, []);

  const handleBuscar = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (busqueda.operation) params.set("operation", busqueda.operation);
    if (busqueda.type) params.set("type", busqueda.type);
    if (busqueda.city) params.set("city", busqueda.city);
    navigate(`/propiedades?${params.toString()}`);
  };

  return (
    <div className="inicio-page">
      <Navbar />

      {/* ── HERO ───────────────────────────────────── */}
      <section className="hero">
        <div className="hero-bg" style={settings.hero_image_url
          ? { backgroundImage: `url(${imgUrl(settings.hero_image_url)})` }
          : {}} />
        <div className="container hero-content">
          <p className="hero-eyebrow">{settings.site_tagline || "Bienes raíces en Córdoba y Veracruz"}</p>
          <h1 className="hero-title heading-serif">
            {settings.hero_title
              ? settings.hero_title.split("\n").map((line, i, arr) => (
                  <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
                ))
              : <>Encuentra tu<br />propiedad ideal</>
            }
          </h1>
          <p className="hero-sub">
            {settings.hero_subtitle || "Casas, departamentos, ranchos y más. Asesoría personalizada sin compromiso."}
          </p>

          {/* Buscador */}
          <form className="hero-search card" onSubmit={handleBuscar}>
            <div className="search-tabs">
              {["VENTA", "RENTA"].map((op) => (
                <button
                  key={op}
                  type="button"
                  className={`search-tab ${busqueda.operation === op ? "active" : ""}`}
                  onClick={() => setBusqueda({ ...busqueda, operation: op })}
                >
                  {op === "VENTA" ? "🏷️ Comprar" : "🔑 Rentar"}
                </button>
              ))}
            </div>
            <div className="search-fields">
              <select
                className="form-select"
                value={busqueda.type}
                onChange={(e) => setBusqueda({ ...busqueda, type: e.target.value })}
              >
                <option value="">Tipo de inmueble</option>
                {TIPOS_PROPIEDAD.map((t) => (
                  <option key={t.value} value={t.value}>{t.emoji} {t.label}</option>
                ))}
              </select>
              <select
                className="form-select"
                value={busqueda.city}
                onChange={(e) => setBusqueda({ ...busqueda, city: e.target.value })}
              >
                <option value="">Todas las ciudades</option>
                {CIUDADES.filter((c) => c !== "Otra").map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <button type="submit" className="btn btn-accent btn-lg">
                🔍 {settings.hero_cta || "Buscar propiedades"}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* ── ESTADÍSTICAS ───────────────────────────── */}
      {stats && (
        <section className="stats-bar">
          <div className="container stats-grid">
            <div className="stat-item">
              <span className="stat-num">{stats.total}+</span>
              <span className="stat-label">Propiedades activas</span>
            </div>
            {stats.porOperacion?.map((s) => (
              <div key={s.operation} className="stat-item">
                <span className="stat-num">{s.count}</span>
                <span className="stat-label">En {s.operation === "VENTA" ? "venta" : "renta"}</span>
              </div>
            ))}
            <div className="stat-item">
              <span className="stat-num">10+</span>
              <span className="stat-label">Años de experiencia</span>
            </div>
          </div>
        </section>
      )}

      {/* ── DESTACADAS ─────────────────────────────── */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-serif">Propiedades Destacadas</h2>
            <p>Selección especial de nuestras mejores opciones</p>
          </div>
          {destacadas.length === 0 ? (
            <div className="empty-state">Cargando propiedades...</div>
          ) : (
            <div className="props-grid">
              {destacadas.map((p) => <PropertyCard key={p.id} propiedad={p} />)}
            </div>
          )}
          <div className="section-cta">
            <a href="/propiedades" className="btn btn-outline">
              Ver todas las propiedades →
            </a>
          </div>
        </div>
      </section>

      {/* ── TIPOS ──────────────────────────────────── */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-serif">Explora por tipo</h2>
          </div>
          <div className="tipos-grid">
            {TIPOS_PROPIEDAD.slice(0, 6).map((t) => (
              <a
                key={t.value}
                href={`/propiedades?type=${t.value}`}
                className="tipo-card card"
              >
                <span className="tipo-emoji">{t.emoji}</span>
                <span className="tipo-label">{t.label}</span>
                {stats?.porCiudad && (
                  <span className="tipo-count">
                    {stats.total > 0 ? "Ver propiedades" : ""}
                  </span>
                )}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── CIUDADES ───────────────────────────────── */}
      {stats?.porCiudad?.length > 0 && (
        <section className="section">
          <div className="container">
            <div className="section-header">
              <h2 className="heading-serif">Explora por ciudad</h2>
            </div>
            <div className="ciudades-grid">
              {stats.porCiudad.map((c) => (
                <a
                  key={c.city}
                  href={`/propiedades?city=${encodeURIComponent(c.city)}`}
                  className="ciudad-card card"
                >
                  <div className="ciudad-info">
                    <h3>{c.city}</h3>
                    <span>{c.count} propiedad{c.count !== 1 ? "es" : ""}</span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA FINAL ──────────────────────────────── */}
      <section className="cta-section">
        <div className="container cta-content">
          <h2 className="heading-serif">¿Buscas vender o rentar tu propiedad?</h2>
          <p>Contáctanos y te ayudamos a encontrar el mejor comprador o arrendatario.</p>
          <a href="/contacto" className="btn btn-accent btn-lg">Contactar ahora</a>
        </div>
      </section>

      <Footer />
      <WhatsAppBtn />
    </div>
  );
}
